/**
 * Locked USD for LDS/package order-details display.
 * Prefer amounts persisted at booking (origCurrency); never use live admin FX
 * when the order has a booking-time fx_rate / currencyRate.
 */

import { resolveLdsOrderOurPriceBlock } from "./resolveLdsOrderHrCommissionIls";

function asPos(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function asPlainObject(raw) {
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
}

/** Number, or `{ USD }` map written by some persist paths. */
function asPosRate(value) {
  const direct = asPos(value);
  if (direct != null) return direct;
  const obj = asPlainObject(value);
  if (!obj) return null;
  return asPos(obj.USD) ?? asPos(obj.usd);
}

function collectLockedFareBlocks(order) {
  const op = resolveLdsOrderOurPriceBlock(order);
  const otf = asPlainObject(op.ourTotalFare) || {};
  const blocks = [otf, op];
  const passengerLists = [
    order.passengersinfo,
    order.passengers_info,
    order.flights && typeof order.flights === "object" && !Array.isArray(order.flights)
      ? order.flights.passengersinfo
      : null,
  ];
  for (const list of passengerLists) {
    if (!Array.isArray(list)) continue;
    for (const entry of list) {
      const room =
        entry?.room && typeof entry.room === "object" ? entry.room : entry;
      const fare =
        room?.ourFareDetails?.ourTotalFare ??
        room?.ourFareDetails?.ourtotalfare ??
        null;
      if (asPlainObject(fare)) blocks.push(fare);
    }
  }
  return { op, otf, blocks };
}

/**
 * Booking-time ILS-per-USD (or deal currency) rate locked on the order.
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {number|null}
 */
export function resolveLdsOrderLockedFxRate(order) {
  if (!order || typeof order !== "object") return null;
  const { blocks } = collectLockedFareBlocks(order);
  for (const block of blocks) {
    const rate =
      asPosRate(block.fx_rate) ??
      asPosRate(block.currencyRate) ??
      asPosRate(block.currency_rate);
    if (rate != null) return rate;
  }
  return (
    asPosRate(order.fx_rate) ??
    asPosRate(order.currencyRate) ??
    asPosRate(order.currency_rate) ??
    null
  );
}

/**
 * Shape for formatOrderDetailsIlsWithUsdSecondary — booking FX only.
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {{ USD: number }|null}
 */
export function resolveLdsOrderLockedCurrencyRateMap(order) {
  const rate = resolveLdsOrderLockedFxRate(order);
  return rate != null ? { USD: rate } : null;
}

/**
 * Authoritative USD amounts locked at pricing/booking (origCurrency).
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {{
 *   totalUsd: number|null,
 *   hrUsd: number|null,
 *   netUsd: number|null,
 *   agentSellUsd: number|null,
 *   agentFeeUsd: number|null,
 *   lockedFxRate: number|null,
 * }}
 */
export function resolveLdsOrderAuthoritativeUsdMap(order) {
  const empty = {
    totalUsd: null,
    hrUsd: null,
    netUsd: null,
    agentSellUsd: null,
    agentFeeUsd: null,
    lockedFxRate: null,
  };
  if (!order || typeof order !== "object") return empty;

  const op = resolveLdsOrderOurPriceBlock(order);
  const otf = asPlainObject(op.ourTotalFare) || {};
  const origCandidates = [
    op.origCurrency,
    otf.origCurrency,
    op.originalCurrency,
    otf.originalCurrency,
    asPlainObject(op.pricingSnapshot)?.origCurrency,
  ].filter((v) => v && typeof v === "object" && !Array.isArray(v));
  const orig = origCandidates[0] || {};

  // Supplier net in foreign currency only from origCurrency (locked at pricing).
  // netPrice is explicit; initialPrice is stamped by runAppContextBaseCommissionPricing
  // as ceil(netForeign). Do not use ourTotalFare.netPrice — it is ILS on some paths.
  const netUsd = asPos(orig.netPrice) ?? asPos(orig.initialPrice);
  const hrUsd = asPos(orig.hrCommission);
  const hrUsdOrZero = Number.isFinite(Number(orig.hrCommission))
    ? Math.max(0, Number(orig.hrCommission))
    : null;
  const agentSellUsd =
    netUsd != null && hrUsdOrZero != null
      ? asPos(netUsd + hrUsdOrZero)
      : asPos(orig.priceWithCommission);

  return {
    totalUsd:
      asPos(orig.priceWithCommissionAndCreditCardFee) ??
      asPos(orig.finalPrice) ??
      asPos(asPlainObject(op.pricingSnapshot)?.final_price_usd),
    hrUsd,
    netUsd,
    agentSellUsd,
    agentFeeUsd:
      asPos(orig.handleFee) ??
      asPos(orig.handlingFee) ??
      asPos(orig.handling_fee) ??
      asPos(orig.agentCommission),
    lockedFxRate: resolveLdsOrderLockedFxRate(order),
  };
}
